//React
import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

//component
// import { useAppSelector } from "../../stores"
// import { FamlaServices } from '../../services'


//interface

// import { iAlert } from '../../interfaces'
import { Spiner } from '../../components'


interface ChildProps {
    processId: string | undefined
    /** Remplace `generic${processId}` pour l’attribut id du nœud modal (ex. suppressions data collection). */
    modalDomId?: string
    /** Après montage, ouvre la modale via l’API Bootstrap (à utiliser avec montage conditionnel, ex. accueil). */
    autoShow?: boolean
    /** Appelé une fois la modale complètement fermée (`hidden.bs.modal`). */
    onHidden?: () => void
    action: (id: string, comapnyId?: string) => Promise<boolean | void> | void | (() => void);
    title: string
    sentence: string
    actionButton: string
    classNameSentence?: string
    alert?: {
        isActive: boolean
        type: string
        message: string
    }
}


const GenericModal = (props: ChildProps) => {
    const { processId, modalDomId, action, title, sentence, actionButton, classNameSentence, alert, autoShow, onHidden } = props
    const resolvedModalDomId = modalDomId ?? `generic${processId ?? ''}`
    const closeRef = useRef<HTMLButtonElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const onHiddenRef = useRef(onHidden);
    onHiddenRef.current = onHidden;
    const [state, setState] = React.useState({
        isSubmit: false,
    })

    const { t } = useTranslation();

    // Nettoyer le backdrop quand le modal est fermé (sécurité supplémentaire)
    useEffect(() => {
        const modalElement = modalRef.current;
        if (!modalElement) return;

        const cleanupBackdrop = () => {
            const backdrops = document.querySelectorAll('.modal-backdrop');
            backdrops.forEach(backdrop => backdrop.remove());
            document.body.classList.remove('modal-open');
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };

        const handleHidden = () => {
            cleanupBackdrop();
            onHiddenRef.current?.();
            // Nettoyer plusieurs fois pour être sûr
            setTimeout(cleanupBackdrop, 50);
            setTimeout(cleanupBackdrop, 200);
        };

        modalElement.addEventListener('hidden.bs.modal', handleHidden);

        return () => {
            modalElement.removeEventListener('hidden.bs.modal', handleHidden);
            // Nettoyage final au démontage du composant
            cleanupBackdrop();
        };
    }, []);

    useEffect(() => {
        if (!autoShow) return;
        const el = modalRef.current;
        if (!el) return;
        const Modal = (window as any).bootstrap?.Modal;
        if (!Modal) return;
        const inst = Modal.getOrCreateInstance(el, { backdrop: 'static' });
        const raf = requestAnimationFrame(() => {
            inst.show();
        });
        return () => {
            cancelAnimationFrame(raf);
            const existing = Modal.getInstance(el);
            if (existing) {
                try {
                    existing.hide();
                } catch {
                    /* ignore */
                }
                existing.dispose();
            }
        };
    }, [autoShow]);

    const cleanupBackdrop = () => {
        // Supprimer tous les backdrops (au cas où il y en aurait plusieurs)
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => {
            backdrop.remove();
        });
        
        // Nettoyer les styles du body
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        
        // Forcer la suppression de tous les modals ouverts
        const openModals = document.querySelectorAll('.modal.show');
        openModals.forEach(modal => {
            const htmlModal = modal as HTMLElement;
            htmlModal.classList.remove('show');
            htmlModal.setAttribute('aria-hidden', 'true');
            htmlModal.style.display = 'none';
        });
    };

    const closeModal = () => {
        if (modalRef.current) {
            const modalElement = modalRef.current;
            const bootstrap = (window as any).bootstrap;
            const modalInstance = bootstrap?.Modal?.getInstance(modalElement);
            
            if (modalInstance) {
                // Écouter l'événement de fermeture pour nettoyer le backdrop
                const handleHidden = () => {
                    cleanupBackdrop();
                    modalElement.removeEventListener('hidden.bs.modal', handleHidden);
                };
                
                modalElement.addEventListener('hidden.bs.modal', handleHidden, { once: true });
                modalInstance.hide();
                
                // Nettoyer aussi immédiatement et après des délais pour être sûr
                setTimeout(cleanupBackdrop, 50);
                setTimeout(cleanupBackdrop, 200);
                setTimeout(cleanupBackdrop, 500);
            } else if (closeRef.current) {
                // Fallback : utiliser le bouton de fermeture
                closeRef.current.click();
                
                // Nettoyer le backdrop immédiatement et plusieurs fois
                cleanupBackdrop();
                setTimeout(cleanupBackdrop, 50);
                setTimeout(cleanupBackdrop, 150);
                setTimeout(cleanupBackdrop, 300);
                setTimeout(cleanupBackdrop, 500);
            }
        }
    };

    const handleSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        setState({ ...state, isSubmit: true });
        if (typeof action === "function") {
            // Si la fonction attend un argument, passe processId
            console.log('[GENERIC MODAL] Calling action...');
            const result = await action(processId ?? "");
            console.log('[GENERIC MODAL] Action result:', result);
            // Only close modal if action returns true (success)
            if (result === true) {
                console.log('[GENERIC MODAL] Closing modal (result === true)');
                
                // Nettoyer le backdrop AVANT de fermer le modal
                cleanupBackdrop();
                
                // Fermer le modal
                closeModal();
                
                // Nettoyer le backdrop plusieurs fois après la fermeture
                setTimeout(cleanupBackdrop, 10);
                setTimeout(cleanupBackdrop, 50);
                setTimeout(cleanupBackdrop, 100);
                setTimeout(cleanupBackdrop, 200);
                setTimeout(cleanupBackdrop, 500);
            } else {
                console.log('[GENERIC MODAL] Keeping modal open (result !== true)');
            }
        }
        setState({ ...state, isSubmit: false });
    };

    return (
        <div ref={modalRef} className="modal" data-bs-backdrop="static" id={resolvedModalDomId} tabIndex={-1} >
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content bg-dark ">
                    <div className="modal-header border-0">
                        <h5 className="modal-title text-white">{title}</h5>
                        <button type="button" className="btn-close bg-white" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        {alert?.isActive && (
                            <div className={`alert alert-${alert.type} mb-3 text-wrap`} role="alert" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                {alert.message}
                            </div>
                        )}
                        <p className={`text-theme text-wrap ${classNameSentence}`}>{sentence}</p>
                    </div>
                    <div className="modal-footer">
                        <button ref={closeRef} type="button" className="btn btn-secondary" data-bs-dismiss="modal">{t('words.close')}</button>
                        {actionButton && <button type="button" onClick={handleSubmit} className="btn btn-primary">{state.isSubmit ? <Spiner className='spinner-border-sm' /> : <>{actionButton}</>}</button>}
                    </div>
                </div>
            </div>
        </div>
    )
}
export default GenericModal 